import { ColumnDef } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import { ProductsHeaderMenu } from 'components/Table'
import { TooltipProps } from 'components/Tooltip'
import { DomainsForTable } from 'features/inventory/domain/components/DomainsTable/types/model'
import { Product } from 'features/product'
import { useAbility } from 'providers/casl'
import { ProductSwitchCell } from '../../components/ProductSwitchCell'
import { useSwitchDomainsProduct } from '../useSwitchDomainsProduct'
import { UseProductSwitchColumns } from './types'

export const useProductSwitchColumns = ({
  tableFilters,
  tableItemsCount,
}: UseProductSwitchColumns): ColumnDef<DomainsForTable>[] => {
  const ability = useAbility()
  const { t: tDomain } = useTranslation('features/domain')
  const { t: tProduct } = useTranslation('features/product')

  const { update } = useSwitchDomainsProduct()

  const productsSwitches: Product[] = [
    'prebidStack',
    'refresher',
    'viewabilityTools',
    'priceGenius',
    'unfilledRecovery',
  ]

  const column = (product: Product): ColumnDef<DomainsForTable> => {
    const noProductAccess: boolean = ability.cannot('access', `Product-${product}`)

    const cannotAccessOrUpdateProductToggle: boolean =
      noProductAccess || ability.cannot('update', `Product-${product}`)

    const handleToggleProductState = (enabled: boolean): void =>
      void update({ enabledValue: enabled, productType: product, terms: tableFilters })

    const composeTooltip = (): TooltipProps | undefined => {
      if (noProductAccess) {
        return {
          content: tProduct('featureUnavailable'),
        }
      } else return undefined
    }

    return {
      accessorKey: `${product}Enabled`,
      cell: props => (
        <ProductSwitchCell
          {...props}
          disabled={cannotAccessOrUpdateProductToggle}
          product={product}
        />
      ),
      header: tProduct(`product.${product}`),
      meta: {
        disabledHeader: cannotAccessOrUpdateProductToggle,
        headerMenu: (
          <ProductsHeaderMenu
            i18nResource={tDomain}
            productState={{
              disabledToggle: cannotAccessOrUpdateProductToggle || tableItemsCount === 0,
              onToggle: handleToggleProductState,
            }}
          />
        ),
        nonClickable: true,
        tooltip: composeTooltip(),
      },
    }
  }

  return productsSwitches.map((product: Product): ColumnDef<DomainsForTable> => column(product))
}
